import type { ComplaintCategory, ComplaintStatus } from '@zeus/index'
import type { Complaint } from '~/gql/complaints/complaints'
import { useQuery } from '@apollo/client/react'
import { Download, MessageSquareWarning, Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Spinner from '~/components/Spinner'
import { Button } from '~/components/ui/button'
import { GQL_COMPLAINTS } from '~/gql/complaints/complaints'
import { useBranding } from '~/helpers/branding/branding-context'
import { DATE_WITH_YEAR, formatSessionDate } from '~/helpers/dates/session-dates'
import { tourAnchor } from '~/helpers/tour/anchors'
import TablePagination from '~/pages/backoffice/TablePagination'
import { FilterSelect, TableToolbar, ToolbarActions } from '~/pages/backoffice/TableToolbar'
import { useClientPagination } from '~/pages/backoffice/use-client-pagination'
import { downloadCSV } from '~/utils/exportCsv/utils'
import { COMPLAINT_CATEGORY_KEYS, COMPLAINT_STATUS_KEYS } from './complaint-labels'
import ComplaintDialog from './ComplaintDialog'
import { complaintsCsv } from './complaints-csv'
import ComplaintsTable from './ComplaintsTable'
import NewComplaintDialog from './NewComplaintDialog'
import { useComplaintActions } from './use-complaint-actions'

/**
 * Registre des demandes et réclamations de l'organisme (#325), aux admins :
 * Qualiopi, indicateurs 31 et 12 (signalements de violences, V10).
 */
function ComplaintsPage() {
  const { t, i18n } = useTranslation()
  const { timezone } = useBranding()
  const { data, loading, refetch } = useQuery(GQL_COMPLAINTS, { fetchPolicy: 'cache-and-network' })
  const [status, setStatus] = useState<ComplaintStatus | ''>('')
  const [category, setCategory] = useState<ComplaintCategory | ''>('')
  const [opened, setOpened] = useState<Complaint | null>(null)
  const [adding, setAdding] = useState(false)
  const actions = useComplaintActions(() => {
    setOpened(null)
    setAdding(false)
    void refetch()
  })

  const complaints = (data?.complaints ?? []).filter(c => (!status || c.status === status) && (!category || c.category === category))
  const { pageRows, pagination } = useClientPagination(complaints, `${status}:${category}`)
  const formatDate = (value: unknown) => formatSessionDate(value, i18n.language, timezone, DATE_WITH_YEAR)

  const exportCsv = () => {
    const { headers, rows } = complaintsCsv(complaints, t, formatDate)
    downloadCSV(t('complaints.title'), headers, rows)
  }

  return (
    <div className="flex w-full flex-col gap-6">
      <p className="text-sm text-muted-foreground" {...tourAnchor('complaints.intro')}>{t('complaints.intro')}</p>
      <TableToolbar>
        <FilterSelect aria-label={t('complaints.column.status')} value={status} onChange={e => setStatus(e.target.value as ComplaintStatus | '')} {...tourAnchor('complaints.filters')}>
          <option value="">{t('complaints.filter.allStatuses')}</option>
          {Object.entries(COMPLAINT_STATUS_KEYS).map(([value, key]) => <option key={value} value={value}>{t(key)}</option>)}
        </FilterSelect>
        <FilterSelect aria-label={t('complaints.column.category')} value={category} onChange={e => setCategory(e.target.value as ComplaintCategory | '')}>
          <option value="">{t('complaints.filter.allCategories')}</option>
          {Object.entries(COMPLAINT_CATEGORY_KEYS).map(([value, key]) => <option key={value} value={value}>{t(key)}</option>)}
        </FilterSelect>
        <ToolbarActions>
          <Button variant="outline" onClick={exportCsv} disabled={complaints.length === 0} {...tourAnchor('complaints.export')}>
            <Download className="mr-2 size-4" />
            {t('complaints.export')}
          </Button>
          <Button onClick={() => setAdding(true)} {...tourAnchor('complaints.new')}>
            <Plus className="mr-2 size-4" />
            {t('complaints.new.button')}
          </Button>
        </ToolbarActions>
      </TableToolbar>

      <div {...tourAnchor('complaints.list')}>
        {loading && !data
          ? <div className="flex justify-center p-10"><Spinner className="size-6" /></div>
          : complaints.length === 0
            ? (
                <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed p-10 text-center text-muted-foreground">
                  <MessageSquareWarning className="size-8" />
                  <p className="text-sm">{t('complaints.empty')}</p>
                </div>
              )
            : (
                <div className="overflow-x-auto rounded-lg border">
                  <ComplaintsTable complaints={pageRows} formatDate={formatDate} onOpen={setOpened} />
                  <TablePagination {...pagination} />
                </div>
              )}
      </div>

      {opened && (
        <ComplaintDialog
          complaint={opened}
          formatDate={formatDate}
          busy={actions.updating}
          onCancel={() => setOpened(null)}
          onSave={input => void actions.update(opened.id, input)}
        />
      )}
      {adding && <NewComplaintDialog busy={actions.creating} onCancel={() => setAdding(false)} onCreate={input => void actions.create(input)} />}
    </div>
  )
}

export default ComplaintsPage
